"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import Image from "next/image";
import { X } from "lucide-react";
import { useScrollLock } from "@/hooks/useScrollLock";
import { useTenant } from "@/lib/tenant";
import { AuthFlow } from "@/components/auth/AuthFlow";
import { TrustPanel } from "@/components/auth/TrustPanel";
interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Custom title replacing "Anmelden oder registrieren" (applies to all non-success states) */
  title?: string;
  /** Custom subtitle for the method step only (other steps keep their specific instructions) */
  subtitle?: string;
  /** Pre-fill the email field (e.g. from chatbot-collected contact info) */
  initialEmail?: string;
  /** Pre-fill the phone field (e.g. from chatbot-collected contact info) */
  initialPhone?: string;
}
export function LoginModal(t0) {
  const $ = _c(31);
  const {
    isOpen,
    onClose,
    onSuccess,
    title,
    subtitle,
    initialEmail,
    initialPhone
  } = t0;
  const tenant = useTenant();
  const modalRef = useRef(null);
  useScrollLock(isOpen);
  let t1;
  let t2;
  if ($[0] !== isOpen || $[1] !== onClose) {
    t1 = () => {
      const handleEscape = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
      }
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    };
    t2 = [isOpen, onClose];
    $[0] = isOpen;
    $[1] = onClose;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onClose) {
    t3 = e_0 => {
      if (modalRef.current && !modalRef.current.contains(e_0.target as Node)) {
        onClose();
      }
    };
    $[4] = onClose;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleBackdropClick = t3;
  if (!isOpen) {
    return null;
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TrustPanel />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <X className="w-5 h-5" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onClose) {
    t6 = <button onClick={onClose} className="absolute right-4 top-4 p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors">{t5}</button>;
    $[8] = onClose;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== tenant.logo.image || $[11] !== tenant.name) {
    t7 = <div className="text-center">{tenant.logo.image ? <div className="w-auto h-10 mx-auto mb-4 flex items-center justify-center"><Image src={tenant.logo.image} alt={tenant.name} width={160} height={40} className="h-10 w-auto object-contain" priority={true} /></div> : <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center bg-tenant-primary"><svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-tenant-primary-foreground" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg></div>}</div>;
    $[10] = tenant.logo.image;
    $[11] = tenant.name;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <div className="relative px-6 pt-6 pb-2">{t6}{t7}</div>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== onClose || $[17] !== onSuccess) {
    t9 = () => {
      onSuccess?.();
      onClose();
    };
    $[16] = onClose;
    $[17] = onSuccess;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== initialEmail || $[20] !== initialPhone || $[21] !== subtitle || $[22] !== t9 || $[23] !== title) {
    t10 = <div className="px-6 pb-6"><AuthFlow variant="modal" onSuccess={t9} title={title} subtitle={subtitle} initialEmail={initialEmail} initialPhone={initialPhone} /></div>;
    $[19] = initialEmail;
    $[20] = initialPhone;
    $[21] = subtitle;
    $[22] = t9;
    $[23] = title;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t8) {
    t11 = <div ref={modalRef} role="dialog" aria-modal="true" className="relative w-full max-w-md lg:max-w-4xl mx-4 bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 flex overflow-hidden">{t4}<div className="flex-1 flex flex-col">{t8}{t10}</div></div>;
    $[25] = t10;
    $[26] = t8;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== handleBackdropClick || $[29] !== t11) {
    t12 = <div data-above-modal={true} className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onClick={handleBackdropClick}>{t11}</div>;
    $[28] = handleBackdropClick;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
}